/**
 * UI-side messaging — a drop-in reimplementation of create-figma-plugin's
 * `emit` / `on` / `once` over the monorepo's relay bridge.
 *
 * Wire format: `{ pluginMessage: { name, args } }`, matching the sandbox side
 * (src/plugin/messaging.ts). Ported components keep calling `emit(name, ...args)`
 * and `on(name, handler)` unchanged — only their import path changes from
 * `@create-figma-plugin/utilities` to this module.
 */


type Handler = (...args: any[]) => void;

const handlers = new Map<string, Set<Handler>>();
let listening = false;

function ensureListener(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('message', (event: MessageEvent) => {
    const pm = (event.data as { pluginMessage?: { name?: string; args?: any[] } } | null)
      ?.pluginMessage;
    if (!pm || typeof pm.name !== 'string') return;
    const set = handlers.get(pm.name);
    if (set) for (const h of [...set]) h(...(pm.args ?? []));
  });
}

/** Send an event up to the plugin sandbox. */
export function emit(name: string, ...args: any[]): void {
  parent.postMessage({ pluginMessage: { name, args } }, '*');
}

/** Subscribe to an event from the plugin sandbox. Returns an unsubscribe fn. */
export function on(name: string, handler: Handler): () => void {
  ensureListener();
  let set = handlers.get(name);
  if (!set) handlers.set(name, (set = new Set()));
  set.add(handler);
  return () => {
    set!.delete(handler);
  };
}

/** Subscribe to the next occurrence of an event only. */
export function once(name: string, handler: Handler): () => void {
  const off = on(name, (...args: any[]) => {
    off();
    handler(...args);
  });
  return off;
}
